import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon, Loader2, Eye, EyeOff } from "lucide-react";

const formSchema = z.object({
  email: z.string().email("Ingrese un email válido"),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres")
});

type FormData = z.infer<typeof formSchema>;

const Login = () => {
  const navigate = useNavigate();
  const {
    login,
    isAuthenticated
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirigir si ya está autenticado
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const loginForm = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const onSubmitLogin = async (data: FormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await login(data.email, data.password);
      // La navegación se maneja en el efecto cuando isAuthenticated cambia
    } catch (error) {
      let message = "Error al iniciar sesión";
      if (error instanceof Error) message = error.message;
      setErrorMessage(message);
      toast.error(message);
      console.error("Error de inicio de sesión:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fillTestCredentials = (type: 'admin' | 'seller') => {
    if (type === 'admin') {
      loginForm.setValue('email', 'admin@outdoorads.com');
      loginForm.setValue('password', 'admin123');
    } else {
      loginForm.setValue('email', 'vendedor@outdoorads.com');
      loginForm.setValue('password', 'vendor123');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F1F1F5]">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1" style={{
          backgroundColor: "#162832"
        }}>
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/lovable-uploads/gestorad-logo.png" 
              alt="GestorAD Logo" 
              className="h-40 w-auto object-contain"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                console.error('Error loading image:', img.src);
                img.onerror = null;
                img.src = '/lovable-uploads/gestorad-logo.png';
              }}
            />
          </div>
          <h2 
            className="text-2xl text-center font-bold mb-2 login-title" 
            style={{ 
              color: '#ffffff !important', 
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.5rem 0'
            }}
          >
            Iniciar Sesión
          </h2>
          <div 
            className="text-center text-sm login-description" 
            style={{ 
              color: '#ffffff !important', 
              opacity: '0.9',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontSize: '0.875rem',
              margin: '0',
              fontWeight: '400'
            }}
          >
            Ingresa con tu cuenta de Gestorad
          </div>
        </CardHeader>
        <CardContent>
          {errorMessage && <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center gap-2">
                <InfoIcon className="h-4 w-4" />
                {errorMessage}
              </AlertDescription>
            </Alert>}
          
          <Form {...loginForm}>
            <form onSubmit={loginForm.handleSubmit(onSubmitLogin)} className="space-y-4">
              <FormField control={loginForm.control} name="email" render={({
              field
            }) => <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Accede utilizando tu correo electrónico" {...field} disabled={isLoading} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={loginForm.control} name="password" render={({
              field
            }) => <FormItem>
                    <FormLabel>Contraseña</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          placeholder="••••••••" 
                          {...field} 
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <Button 
                type="submit" 
                disabled={isLoading} 
                className="w-full text-white hover:opacity-90"
                style={{ backgroundColor: '#07333e' }}
              >
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </> : "Ingresar"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4 pt-0">
          
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
